// mediaLayout.ts — pure layout decisions for a multi-image post.
//
// No React and no DOM, so the layout maths is unit-testable in isolation.
//
// Multi-image posts render as a swipeable DECK rather than a grid: the active photo sits in
// front and its neighbours peek out behind it. That means the frame height must stay CONSTANT
// as you swipe — a per-photo aspect would make the card jump on every slide — so the deck
// picks one stable aspect up front from the photos it holds.

import type { PostImage } from '@/core/models/post';
import { aspectOf } from '../postImages';

/** Matches the backend's MAX_POST_IMAGES. */
export const MAX_TILES = 4;

/** The deck frame stays inside this band so a stack of panoramas or of very tall
 *  portraits still produces a sensible, stable card. */
export const DECK_AR_MIN = 0.8; // 4:5
export const DECK_AR_MAX = 1.6; // 16:10
/** Used when none of the photos carry stored dimensions. */
export const DECK_AR_DEFAULT = 1.2;

const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));

/**
 * One stable aspect ratio for the whole deck, averaged over the photos whose dimensions we
 * know and clamped into the band. Constant across slides by construction.
 */
export function deckAspect(items: PostImage[]): number {
  const ratios = items.map(aspectOf).filter((r): r is number => r !== null);
  if (ratios.length === 0) return DECK_AR_DEFAULT;
  const avg = ratios.reduce((a, b) => a + b, 0) / ratios.length;
  return clamp(avg, DECK_AR_MIN, DECK_AR_MAX);
}

/** The photos the deck actually renders (the rest are unreachable and dropped). */
export function deckItems(items: PostImage[]): PostImage[] {
  return items.slice(0, MAX_TILES);
}

/**
 * Where a photo sits relative to the active one.
 * 0 = front, ±1 = the peeking neighbours, beyond that it is parked behind the stack.
 */
export function slotOf(index: number, active: number): number {
  return index - active;
}

/** Clamp a requested slide into range (the deck does not wrap — the edges are a cue). */
export function clampSlide(next: number, total: number): number {
  return clamp(next, 0, Math.max(0, total - 1));
}
